<template>
  <div class="version-mobile-container p-4 md:p-6">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-800 mb-2">Versi Mobile</h1>
      <p class="text-gray-600">Kelola notifikasi update untuk aplikasi mobile</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>

    <!-- Main Content -->
    <div v-else class="space-y-6">
      <!-- Notifikasi Update Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-start justify-between mb-4">
          <div class="flex-1">
            <h2 class="text-lg font-semibold text-gray-800 mb-1">
              Notifikasi Update Aplikasi
            </h2>
            <p class="text-sm text-gray-600">
              Aktifkan untuk memberi notifikasi kepada pengguna mobile ketika ada versi baru aplikasi
            </p>
          </div>
          
          <!-- Toggle Switch -->
          <div class="ml-4">
            <label class="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                v-model="notificationEnabled" 
                @change="toggleNotification"
                class="sr-only peer"
                :disabled="updating"
              >
              <div class="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        <!-- Status Badge -->
        <div class="mt-4">
          <span 
            :class="notificationEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'" 
            class="px-3 py-1 rounded-full text-sm font-medium"
          >
            {{ notificationEnabled ? 'Aktif' : 'Nonaktif' }}
          </span>
        </div>
      </div>

      <!-- Check Version Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="mb-4">
          <h2 class="text-lg font-semibold text-gray-800 mb-1">
            Cek Versi Aplikasi
          </h2>
          <p class="text-sm text-gray-600">
            Periksa apakah versi aplikasi memerlukan update
          </p>
        </div>
        
        <!-- Check Version Form -->
        <form @submit.prevent="handleCheckVersion" class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label for="checkCurrentVersion" class="block text-sm font-medium text-gray-700 mb-2">
                Versi Saat Ini <span class="text-red-500">*</span>
              </label>
              <input 
                id="checkCurrentVersion"
                v-model="checkVersionForm.currentVersion" 
                type="text" 
                placeholder="contoh: 17.1.1"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                :disabled="checkingVersion"
                required
              >
            </div>
            
            <div>
              <label for="checkPlatform" class="block text-sm font-medium text-gray-700 mb-2">
                Platform <span class="text-red-500">*</span>
              </label>
              <select 
                id="checkPlatform"
                v-model="checkVersionForm.platform"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                :disabled="checkingVersion"
              >
                <option value="ANDROID">Android</option>
                <option value="IOS">iOS</option>
              </select>
            </div>
          </div>
          
          <div>
            <label for="checkBuildNumber" class="block text-sm font-medium text-gray-700 mb-2">
              Build Number (Opsional)
            </label>
            <input 
              id="checkBuildNumber"
              v-model="checkVersionForm.buildNumber" 
              type="text" 
              placeholder="contoh: 123"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              :disabled="checkingVersion"
            >
          </div>
          
          <div class="flex gap-2">
            <button 
              type="submit"
              :disabled="checkingVersion || !checkVersionForm.currentVersion"
              class="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <span v-if="checkingVersion">Memeriksa...</span>
              <span v-else>Cek Versi</span>
            </button>
            
            <button 
              type="button"
              @click="resetCheckVersionForm"
              :disabled="checkingVersion"
              class="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-lg transition duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              Reset
            </button>
          </div>
        </form>
        
        <!-- Check Version Result -->
          <div v-if="checkVersionResult" class="mt-5">
            <div class="flex justify-between items-center gap-2 pr-3 mb-4">
              <div class="flex-grow">
                <div class="flex justify-start items-center gap-2 pl-3">
                  <div>
                    <p class="text-xs font-bold text-yellow-600 uppercase"><i class="el-icon-warning"></i> {{ checkVersionResult.needsUpdate ? 'Update Diperlukan' : 'Versi Terbaru' }}</p>
                  </div>
                  <div v-if="checkVersionResult.forceUpdate">
                    <p class="text-xs font-thin">-</p>
                  </div>
                  <div v-if="checkVersionResult.forceUpdate">
                    <p class="text-xs font-thin"><i class="el-icon-warning"></i> Force Update</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex justify-between items-center">
              <div class="flex justify-start items-center">
                <div class="border-r p-3">
                  <img src="@/assets/img/android.png" style="width: 25px;" alt="android" v-if="checkVersionResult.platform == 'ANDROID'">
                  <img src="@/assets/img/apple.png" style="width: 25px;" alt="ios" v-if="checkVersionResult.platform == 'IOS'">
                </div>
                <div class="border-r p-3">
                  <p class="text-xs font-light leading-tight">Minimum Version</p>
                  <p class="text-sm font-bold">{{ checkVersionResult.minimumVersion }}</p>
                </div>
                <div class="border-r p-3">
                  <p class="text-xs font-light leading-tight">Platform</p>
                  <p class="text-sm font-bold">{{ checkVersionResult.platform }}</p>
                </div>
                <div class="p-3">
                  <p class="text-xs font-light leading-tight">Information</p>
                  <p class="text-sm font-bold">{{ checkVersionResult.message }}</p>
                </div>
              </div>
              <!-- <div>
                <el-link @click="openDetailDialog(checkVersionResult)" type="info" class="mr-3 cursor-pointer">Detail</el-link>
              </div> -->
            </div>
          
            <div class="ml-3 mt-3">
              <el-link :href="checkVersionResult.downloadUrl" target="_blank" type="primary">Download</el-link>
            </div>
          </div>
        <!-- <div v-if="checkVersionResult" class="mt-6 p-4 rounded-lg" :class="checkVersionResult.needsUpdate ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'">
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-1">
              <span v-if="checkVersionResult.needsUpdate" class="text-2xl">⚠️</span>
              <span v-else class="text-2xl">✅</span>
            </div>
            <div class="flex-1">
              <h3 class="font-semibold text-gray-800 mb-2">
                {{ checkVersionResult.needsUpdate ? 'Update Diperlukan' : 'Versi Terbaru' }}
              </h3>
              
              <div class="space-y-2 text-sm">
                <div class="flex justify-between">
                  <span class="text-gray-600">Versi Saat Ini:</span>
                  <span class="font-medium">{{ checkVersionForm.currentVersion }}</span>
                </div>
                
                <div class="flex justify-between">
                  <span class="text-gray-600">Versi Terbaru:</span>
                  <span class="font-medium">{{ checkVersionResult.latestVersion }}</span>
                </div>
                
                <div class="flex justify-between">
                  <span class="text-gray-600">Minimum Version:</span>
                  <span class="font-medium">{{ checkVersionResult.minimumVersion }}</span>
                </div>
                
                <div v-if="checkVersionResult.forceUpdate" class="flex justify-between items-center">
                  <span class="text-gray-600">Force Update:</span>
                  <span class="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">Ya</span>
                </div>
                
                <div v-if="checkVersionResult.message" class="pt-2 border-t border-gray-200">
                  <p class="text-gray-700">{{ checkVersionResult.message }}</p>
                </div>
                
                <div v-if="checkVersionResult.downloadUrl" class="pt-2">
                  <a 
                    :href="checkVersionResult.downloadUrl" 
                    target="_blank" 
                    class="text-blue-600 hover:underline inline-flex items-center gap-1"
                  >
                    <span>🔗</span>
                    <span>Download Update</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div> -->
      </div>

      <!-- Update Version Card -->
      <!-- <div class="bg-white rounded-lg shadow-md p-6">
        <h2 class="text-lg font-semibold text-gray-800 mb-4">
          Update Versi Mobile
        </h2>
        
        <form @submit.prevent="updateVersion" class="space-y-4">
          <div>
            <label for="version" class="block text-sm font-medium text-gray-700 mb-2">
              Nomor Versi Baru
            </label>
            <input 
              id="version"
              v-model="newVersion" 
              type="text" 
              placeholder="contoh: 1.2.3"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              :disabled="updating"
              required
            >
            <p class="mt-1 text-xs text-gray-500">
              Format: major.minor.patch (contoh: 1.0.0)
            </p>
          </div>

          <div>
            <label for="releaseNotes" class="block text-sm font-medium text-gray-700 mb-2">
              Catatan Rilis (Opsional)
            </label>
            <textarea 
              id="releaseNotes"
              v-model="releaseNotes" 
              rows="4"
              placeholder="Apa yang baru di versi ini..."
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              :disabled="updating"
            ></textarea>
          </div>

          <div class="flex items-center">
            <input 
              id="sendNotification"
              v-model="sendNotificationOnUpdate" 
              type="checkbox"
              class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              :disabled="updating || !notificationEnabled"
            >
            <label for="sendNotification" class="ml-2 text-sm text-gray-700">
              Kirim notifikasi kepada semua pengguna setelah update
            </label>
          </div>

          <button 
            type="submit"
            :disabled="updating || !newVersion"
            class="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <span v-if="updating">Memproses...</span>
            <span v-else>Update Versi</span>
          </button>
        </form>
      </div> -->

      <!-- History Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-semibold text-gray-800">
            Riwayat Versi
          </h2>
          
          <!-- Platform Filter -->
          <div class="flex gap-2">
            <button
              @click="selectedPlatform = 'ALL'"
              :class="selectedPlatform === 'ALL' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'"
              class="px-3 py-1 rounded-lg text-sm font-medium transition"
            >
              Semua
            </button>
            <button
              @click="selectedPlatform = 'ANDROID'"
              :class="selectedPlatform === 'ANDROID' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'"
              class="px-3 py-1 rounded-lg text-sm font-medium transition"
            >
              Android
            </button>
            <button
              @click="selectedPlatform = 'IOS'"
              :class="selectedPlatform === 'IOS' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'"
              class="px-3 py-1 rounded-lg text-sm font-medium transition"
            >
              iOS
            </button>
          </div>
        </div>
        
        <div v-if="filteredVersionHistory.length > 0">
          <div v-for="version in filteredVersionHistory" :key="version.id" class="border-b cursor-pointer hover:bg-gray-50 py-5">
            <div class="flex justify-between items-center gap-2 pr-3 mb-4">
              <div class="flex-grow">
                <div class="flex justify-start items-center gap-2 pl-3">
                  <div>
                    <p class="text-xs font-thin"><i class="el-icon-time"></i> {{ formatDate(version.date) }}</p>
                  </div>
                  <div v-if="version.forceUpdate">
                    <p class="text-xs font-thin">-</p>
                  </div>
                  <div v-if="version.forceUpdate">
                    <p class="text-xs font-thin"><i class="el-icon-warning"></i> Force Update</p>
                  </div>
                  <div v-if="version.active">
                    <p class="text-xs font-thin">-</p>
                  </div>
                  <div v-if="version.active">
                    <p class="text-xs font-thin"><i class="el-icon-success"></i> Aktif</p>
                  </div>
                </div>
              </div>
              <div>
                <div>
                  <p class="text-xs font-thin cursor-pointer hover:text-yellow-500" @click="openEditDialog(version)"><i class="el-icon-edit"></i> Edit</p>
                </div>
              </div>
              <div>
                <p class="text-xs font-thin">-</p>
              </div>
              <div>
                <div>
                  <p @click="openDeleteDialog(version)" class="text-xs font-thin cursor-pointer hover:text-red-500"><i class="el-icon-delete-solid"></i> Delete</p>
                </div>
              </div>
            </div>
            <div class="flex justify-between items-center">
              <div class="flex justify-start items-center">
                <div class="border-r p-3">
                  <img src="@/assets/img/android.png" style="width: 25px;" alt="android" v-if="version.platform == 'ANDROID'">
                  <img src="@/assets/img/apple.png" style="width: 25px;" alt="ios" v-if="version.platform == 'IOS'">
                </div>
                <div class="border-r p-3">
                  <p class="text-xs font-light leading-tight">Minimum Version</p>
                  <p class="text-sm font-bold">{{ version.minimumVersion }}</p>
                </div>
                <div class="border-r p-3">
                  <p class="text-xs font-light leading-tight">Platform</p>
                  <p class="text-sm font-bold">{{ version.platform }}</p>
                </div>
                <div class="p-3">
                  <p class="text-xs font-light leading-tight">Information</p>
                  <p class="text-sm font-bold">{{ version.message }}</p>
                </div>
              </div>
              <div>
                <el-link @click="openDetailDialog(version)" type="info" class="mr-3 cursor-pointer">Detail</el-link>
              </div>
            </div>
          
            <div class="ml-3 mt-3">
              <el-link :href="version.downloadUrl" target="_blank" type="primary">Download</el-link>
            </div>
          </div>
        </div>
        
        <div v-else class="text-center py-8 text-gray-500">
          Belum ada riwayat versi
          <span v-if="selectedPlatform !== 'ALL'">untuk platform {{ selectedPlatform }}</span>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <el-dialog 
      title="Konfirmasi Hapus" 
      :visible.sync="deleteDialogVisible" 
      width="500px"
      :close-on-click-modal="false"
    >
      <div class="text-center py-4">
        <div class="mb-4">
          <i class="el-icon-warning text-red-500 text-6xl"></i>
        </div>
        <h3 class="text-lg font-semibold text-gray-800 mb-2">Apakah Anda yakin?</h3>
        <p class="text-gray-600 mb-4">Anda akan menghapus versi berikut:</p>
        
        <div class="bg-gray-50 rounded-lg p-4 mb-4 text-left">
          <div class="space-y-2">
            <div class="flex justify-between">
              <span class="text-gray-600">Platform:</span>
              <span class="font-semibold">{{ deleteVersionData.platform }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Version:</span>
              <span class="font-semibold">{{ deleteVersionData.version }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Status:</span>
              <span :class="deleteVersionData.active ? 'text-green-600' : 'text-gray-600'" class="font-semibold">
                {{ deleteVersionData.active ? 'Aktif' : 'Nonaktif' }}
              </span>
            </div>
          </div>
        </div>
        
        <p class="text-sm text-red-600 font-medium">Tindakan ini tidak dapat dibatalkan!</p>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="deleteDialogVisible = false">Batal</el-button>
        <el-button type="danger" @click="confirmDelete" :loading="deletingVersion">
          <i class="el-icon-delete"></i> Hapus
        </el-button>
      </span>
    </el-dialog>

    <!-- Detail Version Dialog -->
    <el-dialog 
      title="Detail Versi Mobile" 
      :visible.sync="detailDialogVisible" 
      width="60%"
      :close-on-click-modal="false"
    >
      <div class="space-y-4">
        <!-- Version Info Header -->
        <!-- {{ detailVersion }} -->
          <div>
            <div class="flex justify-between items-center gap-2 pr-3 mb-4">
              <div class="flex-grow">
                <div class="flex justify-start items-center gap-2 pl-3">
                  <div>
                    <p class="text-xs font-thin"><i class="el-icon-time"></i> {{ formatDate(detailVersion.date) }}</p>
                  </div>
                  <div v-if="detailVersion.forceUpdate">
                    <p class="text-xs font-thin">-</p>
                  </div>
                  <div v-if="detailVersion.forceUpdate">
                    <p class="text-xs font-thin"><i class="el-icon-warning"></i> Force Update</p>
                  </div>
                  <div v-if="detailVersion.active">
                    <p class="text-xs font-thin">-</p>
                  </div>
                  <div v-if="detailVersion.active">
                    <p class="text-xs font-thin"><i class="el-icon-success"></i> Aktif</p>
                  </div>
                </div>
              </div>
              
            </div>
            <div class="flex justify-between items-center">
              <div class="flex justify-start items-center">
                <div class="border-r p-3">
                  <img src="@/assets/img/android.png" style="width: 25px;" alt="android" v-if="detailVersion.platform == 'ANDROID'">
                  <img src="@/assets/img/apple.png" style="width: 25px;" alt="ios" v-if="detailVersion.platform == 'IOS'">
                </div>
                <div class="border-r p-3">
                  <p class="text-xs font-light leading-tight">Minimum Version</p>
                  <p class="text-sm font-bold">{{ detailVersion.minimumVersion }}</p>
                </div>
                <div class="border-r p-3">
                  <p class="text-xs font-light leading-tight">Platform</p>
                  <p class="text-sm font-bold">{{ detailVersion.platform }}</p>
                </div>
                <div class="p-3">
                  <p class="text-xs font-light leading-tight">Information</p>
                  <p class="text-sm font-bold">{{ detailVersion.message }}</p>
                </div>
              </div>
            </div>
            <div class="ml-3 mt-3">
              <el-link :href="detailVersion.downloadUrl" target="_blank" type="primary">Download</el-link>
            </div>
          </div>


        <!-- Release Notes -->
        <div v-if="detailVersion.notes" class="bg-white border border-gray-200 rounded-lg p-4">
          <div class="flex items-center gap-2 mb-3">
            <i class="el-icon-document text-gray-600 text-lg"></i>
            <h4 class="text-sm font-semibold text-gray-800 uppercase tracking-wide">Release Notes</h4>
          </div>
          <div class="prose prose-sm max-w-none bg-gray-50 rounded p-4 border border-gray-200 max-h-96 overflow-y-auto" v-html="detailVersion.notes"></div>
        </div>

        <!-- No Release Notes -->
        <div v-else class="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
          <i class="el-icon-document text-gray-400 text-3xl mb-2"></i>
          <p class="text-gray-500 text-sm">Tidak ada catatan rilis</p>
        </div>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">Tutup</el-button>
        <el-button type="primary" @click="openEditFromDetail">
          <i class="el-icon-edit"></i> Edit Versi
        </el-button>
      </span>
    </el-dialog>

    <!-- Edit Version Dialog -->
    <el-dialog 
      title="Edit Versi Mobile" 
      :visible.sync="editDialogVisible" 
      width="70%"
      :close-on-click-modal="false"
    >
      <el-form :model="editForm" :rules="editFormRules" ref="editForm" label-width="150px" label-position="left">
        <el-form-item label="Platform" prop="platform">
          <el-select v-model="editForm.platform" placeholder="Pilih Platform" disabled class="w-full">
            <el-option label="Android" value="ANDROID"></el-option>
            <el-option label="iOS" value="IOS"></el-option>
          </el-select>
        </el-form-item>
        
        <div class="grid grid-cols-2 gap-4">
          <el-form-item label="Latest Version" prop="latestVersion">
            <el-input v-model="editForm.latestVersion" placeholder="contoh: 17.1.2"></el-input>
          </el-form-item>
          
          <el-form-item label="Minimum Version" prop="minimumVersion">
            <el-input v-model="editForm.minimumVersion" placeholder="contoh: 17.1.0"></el-input>
          </el-form-item>
        </div>
        
        <el-form-item label="Download URL" prop="downloadUrl">
          <el-input v-model="editForm.downloadUrl" placeholder="https://..."></el-input>
        </el-form-item>
        
        <el-form-item label="Message" prop="message">
          <el-input v-model="editForm.message" type="textarea" :rows="2" placeholder="Update message..."></el-input>
        </el-form-item>
        
        <div class="grid grid-cols-2 gap-4">
          <el-form-item label="Force Update">
            <el-switch v-model="editForm.forceUpdate"></el-switch>
          </el-form-item>
          
          <el-form-item label="Active">
            <el-switch v-model="editForm.active"></el-switch>
          </el-form-item>
        </div>
        
        <el-form-item label="Release Notes">
          <el-input 
            v-model="editForm.releaseNotes" 
            type="textarea" 
            :rows="8" 
            placeholder="Masukkan HTML untuk release notes..."
          ></el-input>
          <small class="text-gray-500">Mendukung HTML tags untuk formatting</small>
        </el-form-item>
        
        <el-form-item label="Preview Release Notes">
          <div class="border border-gray-300 rounded p-4 bg-gray-50 max-h-64 overflow-y-auto" v-html="editForm.releaseNotes"></div>
        </el-form-item>
      </el-form>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="editDialogVisible = false">Batal</el-button>
        <el-button type="primary" @click="submitEditForm" :loading="submittingEdit">Simpan Perubahan</el-button>
      </span>
    </el-dialog>

    <!-- Toast Notification -->
    <transition name="fade">
      <div 
        v-if="showToast" 
        class="fixed bottom-4 right-4 bg-gray-800 text-white px-6 py-3 rounded-lg shadow-lg z-50"
        :class="toastType === 'success' ? 'bg-green-600' : 'bg-red-600'"
      >
        {{ toastMessage }}
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Axios from '@/utils/axiosConfig';

export default {
  name: 'VersionMobileView',
  data() {
    return {
      loading: false,
      updating: false,
      notificationEnabled: false,
      // Check Version Form
      checkVersionForm: {
        currentVersion: '',
        platform: 'ANDROID',
        buildNumber: ''
      },
      checkVersionResult: null,
      checkingVersion: false,
      // Current Version Display
      currentVersion: '',
      currentMinimumVersion: '',
      currentPlatform: 'ANDROID',
      currentForceUpdate: false,
      currentMessage: '',
      currentDownloadUrl: '',
      releaseDate: null,
      totalNotificationsSent: 0,
      newVersion: '',
      releaseNotes: '',
      sendNotificationOnUpdate: true,
      versionHistory: [],
      selectedPlatform: 'ALL',
      showToast: false,
      toastMessage: '',
      toastType: 'success',
      // Detail Dialog
      detailDialogVisible: false,
      detailVersion: {
        id: null,
        platform: '',
        version: '',
        minimumVersion: '',
        forceUpdate: false,
        message: '',
        downloadUrl: '',
        active: false,
        notes: '',
        date: null
      },
      // Delete Dialog
      deleteDialogVisible: false,
      deletingVersion: false,
      deleteVersionData: {
        id: null,
        platform: '',
        version: '',
        active: false
      },
      // Edit Dialog
      editDialogVisible: false,
      submittingEdit: false,
      editForm: {
        platform: 'ANDROID',
        latestVersion: '',
        minimumVersion: '',
        forceUpdate: false,
        message: '',
        downloadUrl: '',
        active: true,
        releaseNotes: ''
      },
      editFormRules: {
        platform: [{ required: true, message: 'Platform harus dipilih', trigger: 'change' }],
        latestVersion: [{ required: true, message: 'Latest version harus diisi', trigger: 'blur' }],
        minimumVersion: [{ required: true, message: 'Minimum version harus diisi', trigger: 'blur' }],
        downloadUrl: [{ required: true, message: 'Download URL harus diisi', trigger: 'blur' }],
        message: [{ required: true, message: 'Message harus diisi', trigger: 'blur' }]
      }
    };
  },
  computed: {
    ...mapState(['host']),
    ...mapState('version', {
      versionInfo: state => state.info,
      versionNumber: state => state.version,
      versionLoading: state => state.loading,
      versionStatus: state => state.status
    }),
    filteredVersionHistory() {
      if (this.selectedPlatform === 'ALL') {
        return this.versionHistory;
      }
      return this.versionHistory.filter(v => v.platform === this.selectedPlatform);
    }
  },
  mounted() {
    // this.loadSettings();
    this.loadVersionHistory();
    this.loadVersionPlatform(this.currentPlatform);
  },
  created() {
    this.$store.dispatch('version/fetchVersion');
  },
  methods: {
    ...mapActions('version', ['fetchVersion', 'fetchVersionPlatform', 'checkVersion', 'manageVersion', 'deleteVersion']),
    
    async loadSettings() {
      this.loading = true;
      try {
        // Simulate API call - replace with actual API endpoint
        const response = await Axios.get(`${this.host}/api/mobile/version/settings`);
        
        if (response.data) {
          this.notificationEnabled = response.data.notificationEnabled || false;
          this.currentVersion = response.data.currentVersion || '';
          this.releaseDate = response.data.releaseDate || null;
          this.totalNotificationsSent = response.data.totalNotificationsSent || 0;
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        // Load default values if API fails
        this.notificationEnabled = false;
        this.showToastMessage('Gagal memuat pengaturan', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async loadVersionPlatform(platform = 'ANDROID') {
      try {
        // Menggunakan fetchVersionPlatform dari store untuk platform tertentu
        const response = await this.fetchVersionPlatform(platform);
        
        // Parse response sesuai struktur API
        if (response && response.data) {
          const versionData = response.data;
          
          // Update Current Version Info Card
          this.currentPlatform = versionData.platform;
          this.currentVersion = versionData.latestVersion;
          this.currentMinimumVersion = versionData.minimumVersion;
          this.currentForceUpdate = versionData.forceUpdate;
          this.currentMessage = versionData.message;
          this.currentDownloadUrl = versionData.downloadUrl;
          this.releaseDate = versionData.updatedAt || versionData.createdAt;
          
          // Transform data untuk platform tertentu
          const platformVersion = {
            id: versionData.id,
            platform: versionData.platform,
            version: versionData.latestVersion,
            minimumVersion: versionData.minimumVersion,
            forceUpdate: versionData.forceUpdate,
            message: versionData.message,
            downloadUrl: versionData.downloadUrl,
            active: versionData.active,
            notes: versionData.releaseNotes,
            date: versionData.updatedAt || versionData.createdAt,
            createdAt: versionData.createdAt,
            updatedAt: versionData.updatedAt
          };
          
          // Update atau tambahkan ke version history
          const existingIndex = this.versionHistory.findIndex(v => v.id === platformVersion.id);
          if (existingIndex >= 0) {
            this.versionHistory[existingIndex] = platformVersion;
          } else {
            this.versionHistory.push(platformVersion);
          }
          
          return platformVersion;
        }
      } catch (error) {
        console.error(`Error loading version for platform ${platform}:`, error);
        this.showToastMessage(`Gagal memuat versi ${platform}`, 'error');
        throw error;
      }
    },

    async loadVersionHistory() {
      try {
        // Menggunakan fetchVersion dari store
        const response = await this.fetchVersion();
        
        // Parse response sesuai struktur API baru
        if (response && response.data) {
          const versionData = Array.isArray(response.data) ? response.data : response;
          
          // Transform data untuk ditampilkan di history
          this.versionHistory = versionData.map(item => ({
            id: item.id,
            platform: item.platform,
            version: item.latestVersion,
            minimumVersion: item.minimumVersion,
            forceUpdate: item.forceUpdate,
            message: item.message,
            downloadUrl: item.downloadUrl,
            active: item.active,
            notes: item.releaseNotes,
            date: item.updatedAt || item.createdAt,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt
          }));
          
          // Set current version dari platform Android yang aktif (sebagai default)
          const activeAndroid = versionData.find(v => v.platform === 'ANDROID' && v.active);
          if (activeAndroid) {
            this.currentVersion = activeAndroid.latestVersion;
            this.releaseDate = activeAndroid.updatedAt || activeAndroid.createdAt;
          }
        }
      } catch (error) {
        console.error('Error loading version history:', error);
        this.showToastMessage('Gagal memuat riwayat versi', 'error');
        this.versionHistory = [];
      }
    },
    
    async toggleNotification() {
      this.updating = true;
      try {
        // API call to update notification setting
        await Axios.post(`${this.host}/api/mobile/version/notification/toggle`, {
          enabled: this.notificationEnabled
        });
        
        this.showToastMessage(
          this.notificationEnabled 
            ? 'Notifikasi update diaktifkan' 
            : 'Notifikasi update dinonaktifkan',
          'success'
        );
      } catch (error) {
        console.error('Error toggling notification:', error);
        // Revert the toggle on error
        this.notificationEnabled = !this.notificationEnabled;
        this.showToastMessage('Gagal mengubah pengaturan notifikasi', 'error');
      } finally {
        this.updating = false;
      }
    },
    
    async updateVersion() {
      // Validate version format
      const versionPattern = /^\d+\.\d+\.\d+$/;
      if (!versionPattern.test(this.newVersion)) {
        this.showToastMessage('Format versi tidak valid. Gunakan format: major.minor.patch', 'error');
        return;
      }
      
      this.updating = true;
      try {
        // API call to update version
        const response = await Axios.post(`${this.host}/api/mobile/version/update`, {
          version: this.newVersion,
          releaseNotes: this.releaseNotes,
          sendNotification: this.sendNotificationOnUpdate && this.notificationEnabled
        });
        
        if (response.data) {
          this.currentVersion = this.newVersion;
          this.releaseDate = new Date();
          
          // Add to history
          this.versionHistory.unshift({
            version: this.newVersion,
            date: new Date(),
            notes: this.releaseNotes,
            notificationSent: this.sendNotificationOnUpdate && this.notificationEnabled
          });
          
          // Reset form
          this.newVersion = '';
          this.releaseNotes = '';
          this.sendNotificationOnUpdate = true;
          
          this.showToastMessage('Versi berhasil diupdate', 'success');
          
          // Reload settings to get updated stats
          await this.loadSettings();
        }
      } catch (error) {
        console.error('Error updating version:', error);
        this.showToastMessage('Gagal mengupdate versi', 'error');
      } finally {
        this.updating = false;
      }
    },
    
    formatDate(date) {
      if (!date) return 'N/A';
      
      const d = new Date(date);
      const options = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      
      return d.toLocaleDateString('id-ID', options);
    },
    
    async changePlatform(platform) {
      this.currentPlatform = platform;
      await this.loadVersionPlatform(platform);
    },
    
    /**
     * Check version dengan parameter langsung
     * @param {Object} params - Parameter check version
     * @param {string} params.currentVersion - Versi saat ini (required)
     * @param {string} params.platform - Platform (ANDROID/IOS) (required)
     * @param {string} params.buildNumber - Build number (optional)
     * @returns {Promise<Object>} Result dari check version
     */
    async checkVersionWithParams({ currentVersion, platform = 'ANDROID', buildNumber = '' }) {
      try {
        // Validate required parameters
        if (!currentVersion) {
          throw new Error('currentVersion is required');
        }
        
        if (!['ANDROID', 'IOS'].includes(platform)) {
          throw new Error('platform must be ANDROID or IOS');
        }
        
        // Build payload
        const payload = {
          currentVersion,
          platform
        };
        
        // Add buildNumber if provided
        if (buildNumber) {
          payload.buildNumber = buildNumber;
        }
        
        // Call store action
        const response = await this.checkVersion(payload);
        
        if (response && response.data) {
          return {
            success: true,
            data: response.data
          };
        }
        
        return {
          success: false,
          message: 'No data returned'
        };
      } catch (error) {
        console.error('Error checking version:', error);
        return {
          success: false,
          message: error.message || 'Failed to check version',
          error
        };
      }
    },
    
    async handleCheckVersion() {
      // Validate form
      if (!this.checkVersionForm.currentVersion) {
        this.showToastMessage('Versi saat ini harus diisi', 'error');
        return;
      }
      
      this.checkingVersion = true;
      this.checkVersionResult = null;
      
      try {
        const result = await this.checkVersionWithParams({
          currentVersion: this.checkVersionForm.currentVersion,
          platform: this.checkVersionForm.platform,
          buildNumber: this.checkVersionForm.buildNumber
        });
        
        if (result.success) {
          this.checkVersionResult = result.data;
          this.showToastMessage('Pengecekan versi berhasil', 'success');
        } else {
          this.showToastMessage(result.message || 'Gagal memeriksa versi', 'error');
        }
      } catch (error) {
        console.error('Error checking version:', error);
        this.showToastMessage('Gagal memeriksa versi', 'error');
      } finally {
        this.checkingVersion = false;
      }
    },
    
    resetCheckVersionForm() {
      this.checkVersionForm = {
        currentVersion: '',
        platform: 'ANDROID',
        buildNumber: ''
      };
      this.checkVersionResult = null;
    },
    
    showToastMessage(message, type = 'success') {
      this.toastMessage = message;
      this.toastType = type;
      this.showToast = true;
      
      setTimeout(() => {
        this.showToast = false;
      }, 3000);
    },
    
    openDetailDialog(version) {
      // Set version untuk ditampilkan di detail dialog
      this.detailVersion = {
        id: version.id,
        platform: version.platform,
        version: version.version,
        minimumVersion: version.minimumVersion,
        forceUpdate: version.forceUpdate,
        message: version.message,
        downloadUrl: version.downloadUrl,
        active: version.active,
        notes: version.notes || '',
        date: version.date
      };
      
      this.detailDialogVisible = true;
    },
    
    openEditFromDetail() {
      // Tutup detail dialog dan buka edit dialog
      this.detailDialogVisible = false;
      
      // Populate edit form dengan data dari detail
      this.editForm = {
        platform: this.detailVersion.platform,
        latestVersion: this.detailVersion.version,
        minimumVersion: this.detailVersion.minimumVersion,
        forceUpdate: this.detailVersion.forceUpdate,
        message: this.detailVersion.message,
        downloadUrl: this.detailVersion.downloadUrl,
        active: this.detailVersion.active,
        releaseNotes: this.detailVersion.notes || ''
      };
      
      this.editDialogVisible = true;
    },
    
    openDeleteDialog(version) {
      // Set version yang akan dihapus
      this.deleteVersionData = {
        id: version.id,
        platform: version.platform,
        version: version.version,
        active: version.active
      };
      
      this.deleteDialogVisible = true;
    },
    
    async confirmDelete() {
      this.deletingVersion = true;
      
      try {
        // Panggil action deleteVersion dari store dengan destructuring
        const deleteVersionAction = this.$store.dispatch('version/deleteVersion', { id: this.deleteVersionData.id });
        await deleteVersionAction;
        
        this.showToastMessage('Versi berhasil dihapus', 'success');
        this.deleteDialogVisible = false;
        
        // Reload version history
        await this.loadVersionHistory();
      } catch (error) {
        console.error('Error deleting version:', error);
        this.showToastMessage('Gagal menghapus versi', 'error');
      } finally {
        this.deletingVersion = false;
      }
    },
    
    openEditDialog(version) {
      // Populate form dengan data version yang dipilih
      this.editForm = {
        platform: version.platform,
        latestVersion: version.version,
        minimumVersion: version.minimumVersion,
        forceUpdate: version.forceUpdate,
        message: version.message,
        downloadUrl: version.downloadUrl,
        active: version.active,
        releaseNotes: version.notes || ''
      };
      
      this.editDialogVisible = true;
    },
    
    async submitEditForm() {
      this.$refs.editForm.validate(async (valid) => {
        if (!valid) {
          this.showToastMessage('Mohon lengkapi form dengan benar', 'error');
          return false;
        }
        
        this.submittingEdit = true;
        
        try {
          const payload = {
            platform: this.editForm.platform,
            latestVersion: this.editForm.latestVersion,
            minimumVersion: this.editForm.minimumVersion,
            forceUpdate: this.editForm.forceUpdate,
            message: this.editForm.message,
            downloadUrl: this.editForm.downloadUrl,
            active: this.editForm.active,
            releaseNotes: this.editForm.releaseNotes
          };
          
          await this.manageVersion(payload);
          
          this.showToastMessage('Versi berhasil diupdate', 'success');
          this.editDialogVisible = false;
          
          // Reload version history
          await this.loadVersionHistory();
        } catch (error) {
          console.error('Error updating version:', error);
          this.showToastMessage('Gagal mengupdate versi', 'error');
        } finally {
          this.submittingEdit = false;
        }
      });
    }
  }
};
</script>

<style scoped>
.version-mobile-container {
  max-width: 1200px;
  margin: 0 auto;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Custom scrollbar for textarea */
textarea::-webkit-scrollbar {
  width: 8px;
}

textarea::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

textarea::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

textarea::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
